// @figma file=pkCGby54zXzTsccXvHqzO6 node=452:4831 name="Steps" set="mytaible-shadcn-studio" verified=2026-03-22

import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Layout variants for the `<Steps>` root `<ol>`.
 *
 * horizontal — flex-row, used for compact wizard-style headers.
 * vertical   — flex-col, used for sidebar progress or tall forms.
 */
export const stepsVariants = cva('flex w-full', {
  variants: {
    orientation: {
      horizontal: 'flex-row items-start',
      vertical: 'flex-col items-stretch',
    },
  },
  defaultVariants: { orientation: 'horizontal' },
});

/**
 * Visual style for the step number / check-mark badge.
 *
 * completed | current → dark foreground background, light text (Figma: fill_2MP61K / fill_GYOQR5).
 * upcoming            → muted background, muted text     (Figma: fill_HSR7VS / fill_Z2OKG0).
 */
export const stepIconVariants = cva(
  [
    'inline-flex items-center justify-center shrink-0 select-none',
    'rounded-[var(--radius)] text-sm font-medium',
    'transition-colors',
    'size-9',
  ],
  {
    variants: {
      status: {
        completed: 'bg-foreground text-background',
        current: 'bg-foreground text-background',
        upcoming: 'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { status: 'upcoming' },
  },
);

/**
 * Label text style keyed by step status.
 */
export const stepLabelVariants = cva('text-sm leading-none', {
  variants: {
    status: {
      completed: 'text-foreground',
      current: 'text-foreground font-medium',
      upcoming: 'text-muted-foreground',
    },
  },
  defaultVariants: { status: 'upcoming' },
});

// ─── Context ─────────────────────────────────────────────────────────────────

interface StepsContextValue {
  orientation: 'horizontal' | 'vertical';
  currentStep: number;
}

const StepsContext = React.createContext<StepsContextValue>({
  orientation: 'horizontal',
  currentStep: 1,
});

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StepsProps
  extends Omit<React.ComponentPropsWithoutRef<'ol'>, 'children'>,
    VariantProps<typeof stepsVariants> {
  /** 1-based index of the currently active step. */
  currentStep: number;
  /** Layout direction. Default: `'horizontal'`. */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children?: React.ReactNode;
}

export interface StepProps extends React.ComponentPropsWithoutRef<'li'> {
  /** Visible label for this step. */
  label: string;
  /** Optional descriptive sub-text shown below the label. */
  description?: string;
  /**
   * Explicit step status. When omitted, status is auto-derived from the
   * parent `<Steps currentStep>` and this step's position.
   */
  status?: 'completed' | 'current' | 'upcoming';
  className?: string;
  /**
   * @internal — injected by `<Steps>` via `React.cloneElement`.
   * Do not set this prop manually.
   */
  _index?: number;
  /**
   * @internal — injected by `<Steps>` via `React.cloneElement`.
   * Do not set this prop manually.
   */
  _total?: number;
}

// ─── Steps (root) ─────────────────────────────────────────────────────────────

/**
 * Multi-step progress indicator for sequential workflow stages.
 *
 * `currentStep` (1-based) drives automatic status derivation for every
 * direct `<Step>` child.  A child may override its own status via
 * `status="completed | current | upcoming"`.
 *
 * ARIA: `<ol aria-label="progress">` root;
 *       `aria-current="step"` is set on the active `<Step>`.
 *
 * @example
 * ```tsx
 * <Steps currentStep={2}>
 *   <Step label="Account" description="Create your account" />
 *   <Step label="Profile" />
 *   <Step label="Review" />
 * </Steps>
 * ```
 */
const Steps = React.forwardRef<HTMLOListElement, StepsProps>(
  ({ className, currentStep, orientation = 'horizontal', children, ...props }, ref) => {
    const childArray = React.Children.toArray(children);
    const total = childArray.length;

    const enhancedChildren = childArray.map((child, index) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(
          child as React.ReactElement<StepProps>,
          { _index: index + 1, _total: total } as Partial<StepProps>,
        );
      }
      return child;
    });

    return (
      <StepsContext.Provider value={{ orientation, currentStep }}>
        <ol
          ref={ref}
          aria-label="progress"
          className={cn(stepsVariants({ orientation }), className)}
          {...props}
        >
          {enhancedChildren}
        </ol>
      </StepsContext.Provider>
    );
  },
);
Steps.displayName = 'Steps';

// ─── Step ─────────────────────────────────────────────────────────────────────

/**
 * Individual step inside a `<Steps>` container.
 *
 * Renders an icon badge (number or check-mark) with a connecting line to
 * adjacent steps.  Status is auto-derived from `<Steps currentStep>` when
 * the `status` prop is not explicitly set.
 *
 * @prop label       — Visible step name.
 * @prop description — Optional subtitle shown below the label.
 * @prop status      — `'completed' | 'current' | 'upcoming'`.
 *                     Auto-derived when omitted.
 */
const Step = React.forwardRef<HTMLLIElement, StepProps>(
  ({ className, label, description, status, _index = 1, _total = 1, ...props }, ref) => {
    const { currentStep, orientation } = React.useContext(StepsContext);

    const resolvedStatus: 'completed' | 'current' | 'upcoming' =
      status ??
      (_index < currentStep ? 'completed' : _index === currentStep ? 'current' : 'upcoming');

    const isFirst = _index === 1;
    const isLast = _index === _total;

    /**
     * Connector fill logic:
     *   left connector — filled once the step itself has been reached (current or completed).
     *   right connector — filled once the step has been completed.
     */
    const leftFilled = _index <= currentStep;
    const rightFilled = _index < currentStep;

    const leftColor = leftFilled ? 'bg-foreground' : 'bg-border';
    const rightColor = rightFilled ? 'bg-foreground' : 'bg-border';

    const iconEl = (
      <div className={cn(stepIconVariants({ status: resolvedStatus }))}>
        {resolvedStatus === 'completed' ? (
          <CheckIcon className="size-4" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">{_index}</span>
        )}
      </div>
    );

    const labelEl = (
      <div
        className={cn(
          'flex flex-col gap-0.5',
          orientation === 'horizontal' ? 'items-center text-center' : 'items-start',
        )}
      >
        <span className={cn(stepLabelVariants({ status: resolvedStatus }))}>{label}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </div>
    );

    // ── Horizontal layout ───────────────────────────────────────────────────
    if (orientation === 'horizontal') {
      return (
        <li
          ref={ref}
          aria-current={resolvedStatus === 'current' ? 'step' : undefined}
          className={cn('flex flex-1 flex-col items-center gap-2 min-w-0', className)}
          {...props}
        >
          {/* Connector row centred on the icon */}
          <div className="flex w-full items-center">
            <div
              aria-hidden="true"
              className={cn('flex-1 h-[2px] transition-colors', isFirst ? 'invisible' : leftColor)}
            />
            {iconEl}
            <div
              aria-hidden="true"
              className={cn('flex-1 h-[2px] transition-colors', isLast ? 'invisible' : rightColor)}
            />
          </div>

          {labelEl}
        </li>
      );
    }

    // ── Vertical layout ─────────────────────────────────────────────────────
    return (
      <li
        ref={ref}
        aria-current={resolvedStatus === 'current' ? 'step' : undefined}
        className={cn('flex flex-row items-stretch gap-3', className)}
        {...props}
      >
        {/* Left column — top connector | icon | bottom connector */}
        <div className="flex flex-col items-center">
          <div
            aria-hidden="true"
            className={cn('w-[2px] h-3 transition-colors', isFirst ? 'invisible' : leftColor)}
          />
          {iconEl}
          <div
            aria-hidden="true"
            className={cn(
              'w-[2px] flex-1 min-h-[16px] transition-colors',
              isLast ? 'invisible' : rightColor,
            )}
          />
        </div>

        {/* Right column — label + description, vertically centred */}
        <div className="flex flex-col justify-center py-1">{labelEl}</div>
      </li>
    );
  },
);
Step.displayName = 'Step';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Step, Steps };
